import {
  useAuth,
  getUserAttributes,
  getUserDisplayName,
} from '@choco-admin/auth';
import {
  Navbar,
  NavbarContent,
  NavbarItem,
  Button,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Chip,
} from '@heroui/react';
import React, { useState, useEffect } from 'react';
import { FiMenu, FiLogOut, FiKey } from 'react-icons/fi';

import { PuppyModeToggle } from '../../components/PuppyModeToggle';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTheme } from '../../contexts/ThemeContext';
import { useDailyCode } from '../hooks/useDailyCode';

interface HeaderProps {
  onToggleSidebar: () => void;
  onToggleMobileMenu: () => void;
  sidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  onToggleMobileMenu,
  sidebarCollapsed,
}) => {
  const { signOut, user } = useAuth();
  const { theme } = useTheme();
  const [userDisplayName, setUserDisplayName] = useState('Admin User');
  const [userInitials, setUserInitials] = useState('AU');

  const dailyCode = useDailyCode();

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const attributes = await getUserAttributes();
        const displayName = getUserDisplayName(attributes);
        setUserDisplayName(displayName);

        // Generate initials from display name
        const names = displayName.split(' ');
        const initials =
          names.length >= 2
            ? `${names[0][0]}${names[names.length - 1][0]}`
            : `${names[0][0]}${names[0][1] || ''}`;
        setUserInitials(initials.toUpperCase());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading user info:', error);
      }
    };

    if (user) {
      loadUserInfo();
    }
  }, [user]);

  const handleMenuClick = () => {
    // On mobile, toggle mobile menu; on desktop, toggle sidebar
    if (window.innerWidth <= 1024) {
      onToggleMobileMenu();
    } else {
      onToggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // User will be redirected to login page by the AuthGuard
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error);
    }
  };

  return (
    <Navbar
      maxWidth="full"
      className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm backdrop-blur-md h-16"
      id="header"
      style={{
        background:
          theme === 'dark'
            ? 'linear-gradient(135deg, rgba(24,0,63,0.95) 0%, rgba(26,13,120,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <NavbarContent justify="start">
        <NavbarItem>
          <Button
            isIconOnly
            variant="light"
            onPress={handleMenuClick}
            aria-label="Toggle menu"
            className="hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            size="lg"
          >
            <FiMenu size={22} className="text-gray-700 dark:text-gray-300" />
          </Button>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent justify="center" className="gap-8">
        <NavbarItem>
          <Chip
            variant="flat"
            color="primary"
            size="lg"
            startContent={<FiKey className="h-4 w-4" />}
            classNames={{
              base: 'px-4 py-2 bg-primary-100 dark:bg-primary-400/25 border border-primary-200 dark:border-primary-500/40',
              content: 'flex items-center gap-2 font-semibold',
            }}
          >
            <span className="text-sm text-primary-700 dark:text-primary-500">
              Code of the day:
            </span>
            <span className="text-base font-bold text-primary-900 dark:text-primary-400 tracking-wider">
              {dailyCode}
            </span>
          </Chip>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent justify="end" className="gap-4">
        <NavbarItem>
          <PuppyModeToggle size="lg" />
        </NavbarItem>
        <NavbarItem>
          <ThemeToggle />
        </NavbarItem>

        {/* User Info Section */}
        <NavbarItem className="hidden lg:flex">
          <div className="flex flex-col items-end mr-3 space-y-1">
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {userDisplayName}
            </span>
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
              Super Admin
            </span>
          </div>
        </NavbarItem>

        <NavbarItem>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Avatar
                size="md"
                name={userInitials}
                className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-all border-2 border-white dark:border-gray-600 shadow-md"
              />
            </DropdownTrigger>
            <DropdownMenu
              aria-label="User menu actions"
              className="w-64"
              itemClasses={{
                base: 'gap-4 p-3',
              }}
            >
              <DropdownItem
                key="profile-info"
                isReadOnly
                className="h-16 opacity-100"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {userDisplayName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Super Administrator
                    </p>
                  </div>
                </div>
              </DropdownItem>
              <DropdownItem
                key="logout"
                color="danger"
                startContent={<FiLogOut size={18} />}
                onPress={handleLogout}
                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <span className="font-medium">Sign Out</span>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarItem>
      </NavbarContent>
    </Navbar>
  );
};

export default Header;
